import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/lib/status";
import { toast } from "sonner";
import { Phone, Navigation, LogOut, RefreshCw, Wrench, StickyNote } from "lucide-react";
import { TechSignature } from "@/components/TechSignature";
import { PhotoStrip, PhotoUpload } from "@/components/Photos";
import { ServiceReportForm } from "@/components/ServiceReport";

const iso = (d) => d.toISOString().slice(0, 10);

export default function TechSchedule() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [from, setFrom] = useState(iso(new Date()));
  const [to, setTo] = useState(iso(new Date()));
  const [allTeams, setAllTeams] = useState(false);
  const [data, setData] = useState(null);

  const load = () => api.get(`/tech/schedule?date_from=${from}&date_to=${to}&all_teams=${allTeams}`)
    .then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [from, to, allTeams]);

  const setRange = (days) => { setFrom(iso(new Date())); setTo(iso(new Date(Date.now() + days * 86400000))); };
  const isToday = from === iso(new Date()) && to === iso(new Date());

  const setStatus = async (r, status) => {
    try { await api.post(`/tech/reservations/${r.id}/status`, { status }); toast.success(status === "done" ? "Označeno hotovo" : status === "missed" ? "Nezastižen" : "Vráceno"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveNote = async (r, tech_note) => {
    try { await api.post(`/tech/reservations/${r.id}/status`, { tech_note }); toast.success("Poznámka uložena – uvidí ji předseda"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const days = useMemo(() => {
    const m = {};
    (data?.rows || []).forEach((r) => { (m[r.date] ||= []).push(r); });
    return Object.entries(m);
  }, [data]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="text-white px-4 py-3 flex items-center gap-3" style={{ background: "#0A2540" }}>
        <span className="okenfix-brand text-xl font-extrabold" style={{ color: "#C5A059" }}>OKENFIX</span>
        <span className="text-sm text-slate-300 flex items-center gap-1"><Wrench className="h-4 w-4" /> Technik{data?.team ? ` · ${data.team}` : ""}</span>
        {data?.service_contact && <span className="text-xs text-slate-300 hidden md:block" data-testid="tech-service-contact">Servisní tým: {data.service_contact}</span>}
        <div className="flex-1" />
        <span className="text-xs text-slate-300 hidden sm:block" data-testid="tech-user">{user?.email}</span>
        <TechSignature user={user} onChanged={refreshUser} />
        <Button size="sm" variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/10" onClick={() => { logout(); nav("/prihlaseni"); }} data-testid="tech-logout"><LogOut className="h-4 w-4" /></Button>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl border p-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Button onClick={() => setRange(0)} className={`h-12 text-base ${isToday ? "" : "bg-white text-[#0A2540] border hover:bg-slate-50"}`} style={isToday ? { background: "#0A2540" } : {}} data-testid="tech-today">Dnes</Button>
            <Button onClick={() => { const d = new Date(Date.now() + 86400000); setFrom(iso(d)); setTo(iso(d)); }} variant="outline" className="h-12 text-base" data-testid="tech-tomorrow">Zítra</Button>
            <Button onClick={() => setRange(7)} variant="outline" className="h-12 text-base" data-testid="tech-week">7 dní</Button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Od</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="tech-from" /></div>
            <div className="space-y-1"><Label className="text-xs">Do</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="tech-to" /></div>
            {data?.team_id && <label className="flex items-center gap-2 text-sm h-9"><Checkbox checked={allTeams} onCheckedChange={(c) => setAllTeams(!!c)} data-testid="tech-all-teams" /> Všechny týmy</label>}
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={load} data-testid="tech-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>

        {!data && <p className="text-slate-500">Načítání…</p>}
        {data && days.length === 0 && <div className="bg-white rounded-xl border p-8 text-center text-slate-500" data-testid="tech-empty">{isToday ? "Dnes nemáte žádné návštěvy." : "V tomto období nejsou žádné rezervace."}</div>}
        {days.map(([date, rows]) => (
          <section key={date} className="space-y-2" data-testid={`tech-day-${date}`}>
            <h2 className="text-lg font-bold text-[#0A2540] sticky top-0 bg-[#F8FAFC] py-1">{formatDate(date)} <span className="text-sm font-normal text-slate-500">· {rows.length} bytů</span></h2>
            {rows.map((r) => <Job key={r.id} r={r} onStatus={setStatus} onNote={saveNote} onReload={load} />)}
          </section>
        ))}
      </main>
    </div>
  );
}

function Job({ r, onStatus, onNote, onReload }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(r.tech_note || "");
  useEffect(() => setNote(r.tech_note || ""), [r.tech_note]);
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address || r.svj_name)}`;
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid={`tech-job-${r.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="w-24 shrink-0"><div className="text-2xl font-bold text-[#0A2540]">{r.start}</div><div className="text-xs text-slate-400">do {r.end}</div></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A2540]">{r.svj_name} · vchod {r.entrance} · byt <span className="text-[#C5A059]">{r.apartment}</span> <span className="text-xs text-slate-400">(patro {r.floor})</span></div>
        {r.owner_name && <div className="text-sm text-slate-600" data-testid={`tech-owner-${r.id}`}>Vlastník: {r.owner_name}</div>}
        <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-sm text-[#1E40AF] inline-flex items-center gap-1 hover:underline" data-testid={`tech-nav-${r.id}`}><Navigation className="h-3.5 w-3.5" /> {r.address} <span className="text-xs text-slate-400">· navigovat</span></a>
        <div className="text-sm mt-1"><b>{r.name || "—"}</b></div>
        {r.note && <div className="text-xs text-slate-500 mt-1">Poznámka: {r.note}</div>}
        {r.team && <div className="text-xs text-slate-400 mt-1">Tým: {r.team} · {r.action_name}</div>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-1 gap-1.5 shrink-0 sm:w-36">
        <a href={`tel:${(r.phone || "").replace(/\s/g, "")}`} className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1 h-10 rounded-md text-white text-sm font-medium" style={{ background: "#1E40AF" }} data-testid={`tech-phone-${r.id}`}><Phone className="h-4 w-4" /> {r.phone}</a>
        <a href={mapsUrl} target="_blank" rel="noreferrer" className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1 h-10 rounded-md border text-sm font-medium text-[#0A2540]" data-testid={`tech-navigate-${r.id}`}><Navigation className="h-4 w-4" /> Navigovat</a>
        <div className="col-span-2 sm:col-span-1 flex justify-center"><StatusBadge status={r.service_status} /></div>
        {r.service_status === "scheduled" ? (
          <>
            <Button size="sm" onClick={() => onStatus(r, "done")} className="h-10" style={{ background: "#065F46" }} data-testid={`tech-done-${r.id}`}>Hotovo</Button>
            <Button size="sm" variant="outline" className="h-10" onClick={() => onStatus(r, "missed")} data-testid={`tech-missed-${r.id}`}>Nezastižen</Button>
          </>
        ) : <Button size="sm" variant="ghost" className="col-span-2 sm:col-span-1" onClick={() => onStatus(r, "scheduled")} data-testid={`tech-reset-${r.id}`}>Vrátit</Button>}
      </div>
    </div>
    <div className="border-t pt-3 space-y-2">
      <ServiceReportForm r={r} onSaved={onReload} />
      <PhotoStrip photos={r.photos} canDelete onDeleted={onReload} testPrefix={`tech-photo-${r.id}`} />
      <div className="flex items-center gap-2 flex-wrap"><PhotoUpload reservationId={r.id} onUploaded={onReload} /><span className="text-xs text-slate-400">Fotky závady / oken – uvidí je předseda</span></div>
      {!editing ? (
        <button onClick={() => setEditing(true)} className="w-full text-left text-sm flex items-start gap-2 text-slate-600 hover:text-[#0A2540]" data-testid={`tech-note-edit-${r.id}`}>
          <StickyNote className="h-4 w-4 mt-0.5 shrink-0 text-[#C5A059]" />
          {r.tech_note ? <span className="whitespace-pre-line" data-testid={`tech-note-text-${r.id}`}>{r.tech_note}</span> : <span className="text-slate-400">Přidat poznámku k bytu (počet oken, závada…)</span>}
        </button>
      ) : (
        <div className="space-y-2">
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="např. 4 okna, 1 balkonové dveře – vyměněno kování, prasklé těsnění v kuchyni" data-testid={`tech-note-input-${r.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { onNote(r, note); setEditing(false); }} style={{ background: "#0A2540" }} data-testid={`tech-note-save-${r.id}`}>Uložit poznámku</Button>
            <Button size="sm" variant="ghost" onClick={() => { setNote(r.tech_note || ""); setEditing(false); }}>Zrušit</Button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function formatDate(d) {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
